<template>
  <div class="wh-screen">
    <global-confirm-box ref="confirmBox" />
    <warehouse-doc-screen
      :loading="loading"
      :mobile="device.isMobile"
      :columns="columns"
      :rows="rows"
      :grouped="grouped"
      :chips="chips"
      :chip="statusFilter"
      :hint="hint"
      :hint-clickable="Boolean(lastStocktakingDate)"
      :actions="actions"
      :empty="empty"
      clickable
      @update:chip="statusFilter = $event"
      @hint="setFiltersToLastEntry"
      @action="startStocktaking"
      @open-row="viewStocktaking($event.raw)"
      @toast="$emit('toast', $event)"
    >
      <template #cell="{ row, column }">
        <span v-if="column.key === 'status'" class="wh-status" :class="row.completed ? 'wh-status--ok' : 'wh-status--info'">
          <v-icon size="14">{{ row.completed ? "mdi-check-circle" : "mdi-clock-outline" }}</v-icon>
          {{ row.statusLabel }}
        </span>
        <template v-else>{{ cellText(row[column.key]) }}</template>
      </template>
      <template #row-actions="{ row }">
        <warehouse-row-button icon="mdi-eye" :label="$t('overview')" tone="pri" action="view" @click="viewStocktaking(row.raw)" />
        <warehouse-row-button v-if="!row.completed" icon="mdi-pencil-outline" :label="$t('edit')" action="edit" @click="editStocktaking(row.raw)" />
        <warehouse-row-button
          v-if="!row.completed"
          icon="mdi-trash-can-outline"
          :label="$t('delete')"
          tone="danger"
          action="delete"
          @click="deleteStocktaking(row.raw)"
        />
      </template>
    </warehouse-doc-screen>

    <list-dialog v-model="listDialog" :edited-item="editedItem" @created="onStocktakingCreated" @updated="onStocktakingUpdated" />
    <list-view-dialog v-model="listViewDialog" :list-id="viewItemId" @completed="onStocktakingCompleted" />
    <completed-list-dialog
      v-model="completedStocktakingDialog"
      :list-id="viewItemId"
      :notes="viewItemNotes"
      :completed-at="viewItemCompletedAt"
    />
  </div>
</template>

<script>
import WarehouseTabList from "@/components/warehouse/tabs/WarehouseTabList.vue";
import { isInDateRange } from "@/utils/warehouseUtils";
import WarehouseDocScreen from "../../docs/WarehouseDocScreen.vue";
import WarehouseRowButton from "../../docs/WarehouseRowButton.vue";

const DOT = { completed: "#15803d", active: "#075985" };

/**
 * Popis (inventura): lista popisa sa izborom stanja (aktivni / završeni), pregledom, izmenom i brisanjem aktivnih,
 * "Novi popis" u traci alata. Nasleđuje učitavanje, filtriranje, štampu, Excel i dijaloge iz starog taba.
 */
export default {
  name: "WarehouseScreenStocktaking",
  extends: WarehouseTabList,
  components: { WarehouseDocScreen, WarehouseRowButton },
  computed: {
    grouped() {
      return (this.filters.warehouse || []).length > 1;
    },
    columns() {
      return [
        { key: "id", label: "ID", mobile: "sub", muted: true, num: true, prefix: "#" },
        { key: "name", label: this.$t("warehouse.document_number"), main: true, mobile: "title" },
        { key: "date", label: this.$t("date"), mobile: "meta" },
        { key: "status", label: this.$t("status"), mobile: "badge" },
      ];
    },
    /** Popisi posle pretrage i perioda (brojači stanja se računaju nad njima). */
    baseItems() {
      const search = (this.filters.search || "").toLowerCase();
      return this.items
        .filter(
          (item) =>
            !search ||
            (item.name && item.name.toLowerCase().includes(search)) ||
            (item.note && item.note.toLowerCase().includes(search))
        )
        .filter((item) => isInDateRange(item, "date", this.filters.dates.from, this.filters.dates.to));
    },
    chips() {
      const completed = this.baseItems.filter((item) => this.isStocktakingCompleted(item)).length;
      return [
        { value: null, label: this.$t("all"), count: this.baseItems.length },
        { value: "active", label: this.$t("active"), count: this.baseItems.length - completed, dot: DOT.active },
        { value: "completed", label: this.$t("completed"), count: completed, dot: DOT.completed },
      ];
    },
    rows() {
      return this.filteredItems.map((item) => {
        const completed = Boolean(this.isStocktakingCompleted(item));
        return {
          key: item.id,
          raw: item,
          completed,
          group: { id: item.warehouse?.id ?? 0, name: this.toLatin(item.warehouse?.name || "") || this.$t("warehouse.unknown") },
          id: item.id,
          name: item.name || "",
          date: this.formatDate(item.date),
          status: completed ? "completed" : "active",
          statusLabel: completed ? this.$t("completed") : this.$t("active"),
          sort: { id: Number(item.id) || 0, date: item.date || "", status: completed ? 1 : 0 },
        };
      });
    },
    hint() {
      return this.lastStocktakingDate ? `${this.$t("last_stocktaking_date")}: ${this.lastStocktakingDate}` : "";
    },
    actions() {
      return [{ key: "new", label: this.$t("new_stocktaking"), icon: "mdi-clipboard-check-outline", tone: "pri" }];
    },
    empty() {
      return {
        icon: "mdi-clipboard-check-outline",
        title: this.items.length ? this.$t("no_stocktaking_for_period") : this.$t("no_stocktaking"),
        text: this.lastStocktakingDate
          ? `${this.$t("last_stocktaking_date")}: ${this.lastStocktakingDate}`
          : this.$t("no_stocktaking_yet"),
      };
    },
    shownKpis() {
      if (this.loading) {
        return [];
      }
      const active = this.baseItems.length - this.chips[2].count;
      return [
        { key: "lists", label: this.$t("stocktaking"), value: this.rows.length, tone: "inverse" },
        { key: "active", label: this.$t("active"), value: active, tone: active ? "accent" : "" },
      ];
    },
  },
  watch: {
    shownKpis: {
      handler(kpis) {
        this.$emit("kpis", kpis);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    cellText(value) {
      return value === undefined || value === null || value === "" ? "—" : value;
    },
  },
};
</script>
